'use strict';
/* ==========================================================================
   core-local/thermo.js — "The Big Drop II: Where Did the Heat Go?": the heat model (DESIGN.md §4).
   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/thermocheck.js) and in the page
   (window.Thermo, window.Heat). Registers itself with the question bank as `thermo` when core/bank.js is loaded
   first (lesson.json scripts order), so pool / labs.json formulas can say `thermo.mix(200, 80, 600, 20)`.

   THE HONESTY RULE. Every number this file produces comes from
     - Thermo.constants: sourced values, each {value, unit, row} citing docs/topics/facts-heat.md, or
     - Thermo.assumed:   inputs we chose, each {value, unit, assumed: true, label, row?}. Anything shown on screen
                         that depends on one must carry its label ("our example", "our model").
   tools/thermocheck.js proves every constant equals its facts row and every worked example of facts §6.

   UNITS: SI inside (kg, m, s, J, W), temperatures in °C. Function arguments say their units in THERMO.md.
   Drawing is a pure function of time: every simulation is precomputed (or closed-form) and exposes sample(t);
   sample never steps hidden state.

   The full API contract for the level agents: core-local/THERMO.md.
   ========================================================================== */
(function (root) {
  /* ======================================================================
     1. Constants (sourced) and assumptions (ours)
     ====================================================================== */
  const J_KG_C = 'J/(kg·°C)', W_M_C = 'W/(m·°C)', KG_M3 = 'kg/m³';
  const K = (value, unit, row, note) => (note ? { value, unit, row, note } : { value, unit, row });
  const CONST = {
    // 3a specific heat c
    'c.water': K(4180, J_KG_C, '3.1'), 'c.ice': K(2050, J_KG_C, '3.2'), 'c.aluminum': K(900, J_KG_C, '3.3'),
    'c.steel': K(450, J_KG_C, '3.4'), 'c.stainless': K(500, J_KG_C, '3.5'), 'c.copper': K(385, J_KG_C, '3.6'),
    'c.pine': K(1630, J_KG_C, '3.7'), 'c.oak': K(1630, J_KG_C, '3.7'), 'c.hdpe': K(2100, J_KG_C, '3.8'),
    'c.pmma': K(1420, J_KG_C, '3.9'), 'c.sand': K(800, J_KG_C, '3.10'), 'c.air': K(1006, J_KG_C, '3.11'),
    'c.foam': K(1300, J_KG_C, '3.38'), 'c.wool': K(1260, J_KG_C, '3.43'), 'c.cotton': K(1340, J_KG_C, '3.43'),
    'c.brass': K(385, J_KG_C, '3.45'), 'c.skin': K(3391, J_KG_C, '3.26'),
    // 3b change of state
    'Lf': K(334000, 'J/kg', '3.12'), 'Lv': K(2256000, 'J/kg', '3.13'),
    'T.melt': K(0, '°C', '3.14'), 'T.boil': K(99.97, '°C', '3.15', 'say "about 100 °C"'),
    'T.boilClass': K(100, '°C', '1.13', 'the SSA glossary\'s sea-level value; the model\'s plateau'),
    'T.boilBritton': K(99.6, '°C', '3.17'),
    // 3c conductivity k
    'k.aluminum': K(237, W_M_C, '3.19'), 'k.steel': K(50, W_M_C, '3.20'), 'k.stainless': K(16, W_M_C, '3.21'),
    'k.copper': K(401, W_M_C, '3.22'), 'k.pine': K(0.15, W_M_C, '3.23'), 'k.oak': K(0.17, W_M_C, '3.23'),
    'k.hdpe': K(0.48, W_M_C, '3.24'), 'k.pmma': K(0.19, W_M_C, '3.25'), 'k.skin': K(0.37, W_M_C, '3.26'),
    'k.ice': K(2.2, W_M_C, '3.27'), 'k.water': K(0.607, W_M_C, '3.27'), 'k.air': K(0.026, W_M_C, '3.27'),
    'k.foam': K(0.035, W_M_C, '3.36'), 'k.xps': K(0.029, W_M_C, '3.40'), 'k.pu': K(0.03, W_M_C, '3.41'),
    'k.wool': K(0.04, W_M_C, '3.42'), 'k.cotton': K(0.04, W_M_C, '3.42'), 'k.brass': K(189, W_M_C, '3.45'),
    // 3c density ρ
    'rho.aluminum': K(2700, KG_M3, '3.19'), 'rho.steel': K(7850, KG_M3, '3.20'), 'rho.stainless': K(8000, KG_M3, '3.21'),
    'rho.copper': K(8930, KG_M3, '3.22'), 'rho.pine': K(600, KG_M3, '3.23'), 'rho.oak': K(700, KG_M3, '3.23'),
    'rho.hdpe': K(955, KG_M3, '3.24'), 'rho.pmma': K(1190, KG_M3, '3.25'), 'rho.skin': K(1109, KG_M3, '3.26'),
    'rho.water': K(997, KG_M3, '3.27'), 'rho.ice': K(917, KG_M3, '3.27'), 'rho.air': K(1.2, KG_M3, '3.27'),
    'rho.foam': K(20, KG_M3, '3.37'), 'rho.pu': K(30, KG_M3, '3.41'), 'rho.brass': K(8740, KG_M3, '3.45'),
    // 3d effusivity as printed in row 3.28 (the model computes √(kρc) itself; thermocheck compares)
    'e.copper': K(37100, 'J/(m²·°C·s^½)', '3.28'), 'e.aluminum': K(24000, 'J/(m²·°C·s^½)', '3.28'),
    'e.steel': K(13300, 'J/(m²·°C·s^½)', '3.28'), 'e.stainless': K(8000, 'J/(m²·°C·s^½)', '3.28'),
    'e.water': K(1590, 'J/(m²·°C·s^½)', '3.28'), 'e.skin': K(1180, 'J/(m²·°C·s^½)', '3.28'),
    'e.skinG29': K(1517, 'J/(m²·°C·s^½)', '3.28'), 'e.hdpe': K(980, 'J/(m²·°C·s^½)', '3.28'),
    'e.pmma': K(570, 'J/(m²·°C·s^½)', '3.28'), 'e.oak': K(440, 'J/(m²·°C·s^½)', '3.28'),
    'e.pine': K(380, 'J/(m²·°C·s^½)', '3.28'), 'e.air': K(6, 'J/(m²·°C·s^½)', '3.28'),
    'e.foam': K(30, 'J/(m²·°C·s^½)', '3.39'), 'e.brass': K(25200, 'J/(m²·°C·s^½)', '3.45'),
    // 3d burn thresholds, 1 s contact
    'burn1s.metal': K(65, '°C', '3.31'), 'burn1s.painted': K(83, '°C', '3.31'), 'burn1s.plastic': K(85, '°C', '3.31'),
    'burn1s.wood': K(110, '°C', '3.31'),
    // 3e body
    'T.finger': K(33, '°C', '3.33'), 'T.core': K(37, '°C', '3.34'),
    // 3f Florida air
    'T.orlandoJuly': K(33.3, '°C', '3.49', 'NWS Orlando July normal high, 92.0 °F'), 'F.orlandoJuly': K(92, '°F', '3.49'),
    'T.tampaJuly': K(32.8, '°C', '3.49'),
    // 5 sparkler
    'T.sparkler': K(1000, '°C', '5.15', 'say "about 1,000 °C or more"'),
    // 6.10 The Big Drop's cart (the energy lesson's numbers)
    'cart.m': K(500, 'kg', '6.10'), 'cart.h': K(20, 'm', '6.10'), 'cart.g': K(10, 'm/s²', '6.10'), 'cart.E': K(100000, 'J', '6.10'),
    // 7 coasters (Kanonen, Liseberg)
    'fin.warmMin': K(9, '°C', '7.2'), 'fin.warmMax': K(13, '°C', '7.2'), 'fin.L': K(0.825, 'm', '7.3'),
    'fin.h': K(0.2, 'm', '7.3'), 'fin.b': K(0.006, 'm', '7.3'), 'fin.pairs': K(9, '', '7.3'),
    'kanonen.m': K(7000, 'kg', '7.3'), 'kanonen.v': K(12, 'm/s', '7.3'), 'kanonen.KE': K(504000, 'J', '7.3'),
    'kanonen.finMass': K(155.7, 'kg', '3.46'), 'kanonen.evenWarm': K(8.4, '°C', '3.46'),
    'fin.coolRate': K(0.10, '°C/s', '7.4', 'measured cooling of a fin right after a train'),
    'drop200.m': K(60.96, 'm', '7.11'), 'drop200.perKg': K(597, 'J/kg', '7.11'), 'g': K(9.8, 'm/s²', '7.11'),
  };
  // label: what the student reads next to anything built on it (plain words: no row numbers, no symbols a 7th grader
  // has not met); row / note: where it comes from, for us (tools/thermocheck.js checks both)
  const A = (value, unit, label, row, note) => ({ value, unit, assumed: true, label, ...(row ? { row } : {}), ...(note ? { note } : {}) });
  const ASSUMED = {
    // air films (row 3.44 gives the ranges; the single values are our choice)
    'h.still': A(10, 'W/(m²·°C)', 'still air, convection and radiation (our model)', '3.44', 'convection + radiation film coefficient, inside the row 3.44 range'),
    'h.breeze': A(25, 'W/(m²·°C)', 'a light breeze, about 2 m/s (our model)', '3.44'),
    // rooms and shade
    'T.room': A(22, '°C', 'a room or shade (our example)', '3.50'),
    'T.roomBath': A(20, '°C', 'room temperature for the drop and the bath (our example)', '6.7'),
    // ice
    'm.cube': A(0.015, 'kg', 'our example: a 1-inch ice cube, 15 g', '3.47'),
    'm.pop': A(0.05, 'kg', 'a typical ice pop (about 50 g)', '3.48'),
    'T.iceStart': A(0, '°C', 'our model starts every piece of ice at 0 °C (freezer ice starts colder, which only slows melting)'),
    'film.t': A(0.0001, 'm', 'our model: a 0.1 mm film of meltwater between the cube and the block', '3.27', 'the film conducts like water (k of water, row 3.27); the one tuned assumption (THERMO.md §4)'),
    'block.w': A(0.10, 'm', 'our example: blocks 10 cm × 10 cm, 2 cm thick'),
    'block.h': A(0.02, 'm', 'our example: blocks 10 cm × 10 cm, 2 cm thick'),
    'm.iceBlock': A(1, 'kg', 'our example: a 1 kg block of ice'),
    // fabrics (row 3.42: k only; the thicknesses are ours)
    't.hoodie': A(0.004, 'm', 'our example: a 4 mm fleece hoodie (dry)', '3.42'),
    't.towel': A(0.003, 'm', 'our example: a 3 mm cotton towel (dry)', '3.42'),
    // stove
    'P.burner': A(1000, 'W', 'a steady burner: 1000 W reaching the pot (our model)', '6.8'),
    'P.high': A(2000, 'W', 'turned up high: 2000 W reaching the pot (our model)', '6.8', 'row 6.8: the burner power is an assumption (twice the steady one here); row 5.10: more power boils it away faster, not hotter'),
    // drop vs bath (row 6.7)
    'm.drop': A(0.00005, 'kg', 'our example: a 0.05 g drop', '6.7'), 'T.drop': A(100, '°C', 'a drop of boiling water (about 100 °C)', '6.7'),
    'm.bath': A(150, 'kg', 'our example: a 150 L bath', '6.7'), 'T.bath': A(38, '°C', 'a warm bath at 38 °C (our example)', '6.7'),
    // the fin scene
    'T.finAir': A(22, '°C', 'the air in the brake shed (our example)'),
    'fin.dT0': A(12, '°C', 'the fin starts 12 °C warmer than the air (inside the measured 9 to 13 °C)', '7.2'),
    'fin.airVolume': A(48, 'm³', 'our model: the fin shares the air of a 4 m × 4 m × 3 m brake shed'),
    // the cooler game (sizes and prices invented, labeled "Our model: sizes and prices are invented")
    'cooler.outer': A([0.30, 0.22, 0.22], 'm', 'our cooler: 30 cm × 22 cm × 22 cm outside (invented)'),
  };
  const cv = name => { const e = CONST[name] || ASSUMED[name]; if (!e) throw new Error(`thermo: no constant "${name}"`); return e.value; };

  /* ---------------- materials ---------------- */
  const MAT_NAMES = {
    aluminum: 'aluminum', steel: 'steel', stainless: 'stainless steel', copper: 'copper', pine: 'pine', oak: 'oak',
    hdpe: 'plastic (HDPE)', pmma: 'acrylic', foam: 'foam (EPS)', brass: 'brass', skin: 'skin', water: 'water', ice: 'ice',
    air: 'air', wool: 'fleece', cotton: 'cotton towel',
  };
  const MATERIALS = {};
  for (const id of Object.keys(MAT_NAMES)) {
    const has = p => (CONST[p + '.' + id] ? CONST[p + '.' + id].value : null);
    const m = { id, name: MAT_NAMES[id], c: has('c'), k: has('k'), rho: has('rho'), rows: {} };
    for (const p of ['c', 'k', 'rho']) if (CONST[p + '.' + id]) m.rows[p] = CONST[p + '.' + id].row;
    m.e = m.k !== null && m.rho !== null && m.c !== null ? Math.sqrt(m.k * m.rho * m.c) : null;   // effusivity √(kρc)
    m.alpha = m.e !== null ? m.k / (m.rho * m.c) : null;                                       // diffusivity
    MATERIALS[id] = Object.freeze(m);
  }
  const mat = id => { const m = typeof id === 'object' ? id : MATERIALS[id === 'wood' ? 'pine' : id === 'plastic' ? 'hdpe' : id === 'metal' ? 'aluminum' : id]; if (!m) throw new Error(`thermo: no material "${id}"`); return m; };
  const c = {}, k = {}, rho = {};
  for (const m of Object.values(MATERIALS)) { if (m.c !== null) c[m.id] = m.c; if (m.k !== null) k[m.id] = m.k; if (m.rho !== null) rho[m.id] = m.rho; }
  c.wood = c.pine; c.plastic = c.hdpe;
  const Lf = cv('Lf'), Lv = cv('Lv'), CW = c.water, CI = c.ice;

  /* ======================================================================
     2. Small numbers helpers (text for working lines)
     ====================================================================== */
  const r2 = x => Math.sign(x) * Math.round(Number((Math.abs(x) * 100).toPrecision(12))) / 100 + 0;   // half away from zero
  const roundTo = (x, d = 0) => { const f = Math.pow(10, d); return Math.sign(x) * Math.round(Number((Math.abs(x) * f).toPrecision(12))) / f + 0; };
  function num(v, d = 2) {                        // "12,000", "0.44", "−3.5": ≤ d decimals, zeros trimmed, commas, U+2212
    const x = roundTo(+v, d), neg = x < 0, a = Math.abs(x);
    let [ip, fp] = a.toFixed(d).split('.');
    ip = ip.replace(/\B(?=(\d{3})+$)/g, ',');
    fp = (fp || '').replace(/0+$/, '');
    return (neg ? '−' : '') + ip + (fp ? '.' + fp : '');
  }

  /* ======================================================================
     3. Mixing (rows 6.1–6.6), spread (7.14), the energy bars
     ====================================================================== */
  // water with water: the mass-weighted mean (any mass unit, as long as both use it)
  const mix = (m1, T1, m2, T2) => (m1 * T1 + m2 * T2) / (m1 + m2);
  const avg = (a, b) => (a + b) / 2;
  // a block (m kg, c J/(kg·°C), T) dropped into mw kg of water at Tw (row 6.4); no losses, no change of state
  const mixBlock = (m, cc, T, mw, Tw) => (m * cc * T + mw * CW * Tw) / (m * cc + mw * CW);
  // energy in J to change m kg of a material (or c value) by dT
  const energy = (m, cc, dT) => m * (typeof cc === 'number' ? cc : mat(cc).c) * dT;
  // the temperature change when Q joules go into m kg of c (row 7.14 method)
  const spread = (Q, m, cc) => Q / (m * (typeof cc === 'number' ? cc : mat(cc).c));
  // energy given off by m kg of water cooling from T to Troom (row 6.7)
  const giveOff = (m, T, Troom) => m * CW * (T - Troom);
  // the energy chart's missing bar: TOTAL − PE − KE
  const heatBar = (total, pe, ke) => total - pe - ke;

  /* step-by-step working lines: given(kind, ...args) → [{text, value, line}] (text = the expression, line = "text = value") */
  function given(kind, ...a) {
    const L = (text, value, post = '') => ({ text, value, line: `${text} = ${num(value)}${post}` });
    if (kind === 'mix') {
      const [m1, T1, m2, T2] = a;
      return [L(`${num(m1)} × ${num(T1)}`, m1 * T1), L(`${num(m2)} × ${num(T2)}`, m2 * T2),
        L(`${num(m1 * T1)} + ${num(m2 * T2)}`, m1 * T1 + m2 * T2), L(`${num(m1)} + ${num(m2)}`, m1 + m2),
        L(`${num(m1 * T1 + m2 * T2)} ÷ ${num(m1 + m2)}`, mix(m1, T1, m2, T2), ' °C')];
    }
    if (kind === 'avg') { const [T1, T2] = a; return [L(`${num(T1)} + ${num(T2)}`, T1 + T2), L(`${num(T1 + T2)} ÷ 2`, avg(T1, T2), ' °C')]; }
    if (kind === 'mixBlock') {
      const [m, cc, T, mw, Tw] = a, C1 = m * cc, C2 = mw * CW;
      return [L(`${num(m)} × ${num(cc)}`, C1), L(`${num(mw)} × ${num(CW)}`, C2), L(`${num(C1)} × ${num(T)} + ${num(C2)} × ${num(Tw)}`, C1 * T + C2 * Tw),
        L(`${num(C1)} + ${num(C2)}`, C1 + C2), L(`${num(C1 * T + C2 * Tw)} ÷ ${num(C1 + C2)}`, mixBlock(m, cc, T, mw, Tw), ' °C')];
    }
    if (kind === 'spread') { const [Q, m, cc] = a; return [L(`${num(m)} × ${num(cc)}`, m * cc), L(`${num(Q)} ÷ ${num(m * cc)}`, spread(Q, m, cc), ' °C')]; }
    if (kind === 'heat') { const [tot, pe, ke] = a; return [L(`${num(pe)} + ${num(ke)}`, pe + ke), L(`${num(tot)} − ${num(pe + ke)}`, heatBar(tot, pe, ke), ' J')]; }
    throw new Error(`thermo.given: unknown kind "${kind}"`);
  }

  /* ======================================================================
     4. Touch: contact temperature (rows 3.28–3.30, 3.50)
     ====================================================================== */
  const eOf = x => (typeof x === 'number' ? x : mat(x).e);
  // the interface temperature of two semi-infinite bodies put in perfect contact (row 3.29); e = effusivity or material id
  const contact = (eA, TA, eB, TB) => { const a = eOf(eA), b = eOf(eB); return (a * TA + b * TB) / (a + b); };
  // what a 33 °C finger (skin e from IT'IS, row 3.26) feels on material at T: the contact temperature
  const feels = (m, T, skinT = cv('T.finger')) => contact('skin', skinT, m, T);
  // the heat flux (W/m²) out of the skin into the material, t seconds after touching (semi-infinite bodies):
  //   q = (e_s e_m / (e_s + e_m)) (T_skin − T) / √(π t);  positive = heat leaving the hand
  const touchFlux = (m, T, t = 1, skinT = cv('T.finger')) => { const es = eOf('skin'), em = eOf(m); return es * em / (es + em) * (skinT - T) / Math.sqrt(Math.PI * Math.max(t, 1e-6)); };
  // the direction of heat flow between two named things → "From the soup to the spoon" (warmer first)
  function flow(a, Ta, b, Tb) {
    if (Math.abs(Ta - Tb) < 1e-9) return 'No heat flows: same temperature';
    return Ta > Tb ? `From the ${a} to the ${b}` : `From the ${b} to the ${a}`;
  }
  const warmer = (a, Ta, b, Tb) => (Ta > Tb ? a : Tb > Ta ? b : 'neither');

  /* ======================================================================
     5. exchange: two lumped bodies sharing heat through a conductance (closed form)
     ====================================================================== */
  // a, b: {C (J/°C) | m (kg) + c (J/(kg·°C) or material id), T (°C)}; C may be Infinity (a reservoir). hA: W/°C.
  const capOf = x => (x.C !== undefined ? x.C : x.m * (typeof x.c === 'number' ? x.c : mat(x.c || x.material).c));
  function exchange(a, b, hA) {
    const Ca = capOf(a), Cb = capOf(b), Ta0 = a.T, Tb0 = b.T;
    if (!(hA > 0) || !(Ca > 0) || !(Cb > 0)) throw new Error('thermo.exchange: positive capacities and hA');
    const inf = !isFinite(Cb), invA = 1 / Ca, invB = inf ? 0 : 1 / Cb;
    const Tf = inf ? Tb0 : (Ca * Ta0 + Cb * Tb0) / (Ca + Cb), rate = hA * (invA + invB), tau = 1 / rate;
    const d0 = Ta0 - Tb0;
    const out = {
      Ca, Cb, Tf, tau, hA, T0: [Ta0, Tb0],
      // heat moved from a to b by time t (J; negative = from b to a); given by a = taken by b exactly
      sample(t) {
        t = Math.max(0, t);
        const d = d0 * Math.exp(-rate * t), Q = hA * d0 * (1 - Math.exp(-rate * t)) / rate;
        const Ta = Ta0 - Q * invA, Tb = inf ? Tb0 : Tb0 + Q * invB;
        return { t, Ta, Tb, dT: d, Q, given: Q, taken: Q, Tf, flowW: hA * d, dir: d > 0 ? 'a→b' : d < 0 ? 'b→a' : 'none' };
      },
      // seconds until |Ta − Tb| < tol (°C)
      tSame(tol = 0.1) { return Math.abs(d0) <= tol ? 0 : Math.log(Math.abs(d0) / tol) / rate; },
    };
    return out;
  }

  /* ======================================================================
     6. Slab: 2-D conduction in a round block (axisymmetric r–z finite volumes, explicit), + iceRace
     ====================================================================== */
  /* Why round: a cube on a square block is 3-D. A flat 2-D slice (an infinitely long block) over-spreads the heat in
     poor conductors and washes out the cold spot under the cube. An axisymmetric grid models a ROUND block of the same
     top area with a ROUND patch of the same area as the cube's footprint: the spreading under the cube is then exact
     3-D (for that shape) at the cost of a 2-D grid. Drawers mirror it: column x (−R … R) uses ring i = |x| / dr.
     z: layers from the top, finer near the top (where the cold spot is). Bottom face: on a table, insulated (assumed).
     Top (outside the patch) and the rim: to room air through h (still air, assumed). Under the patch: to the ice at
     0 °C through half a cell + a meltwater film (film.t, assumed; 0 = ideal contact). Explicit time step: 0.9 × the
     stability limit min(C_i / ΣG_i). Once the ice is gone and the block is uniform to 0.02 °C, it warms as one lump
     (the exact exponential), so hours of model time cost nothing. */
  const DZ_DEFAULT = [0.5, 0.5, 1, 1, 1.5, 2, 2.5, 3, 4, 4];   // mm, sums to 20
  class Slab {
    /* o: {material, w = 0.10 (m, square block side → round block of equal area), h = 0.02 (m, thickness, = Σdz),
           dr = 0.002, dz = [mm...] (layers top → bottom, scaled to h), T0 = T.room, Tair = T.room, hAir = h.still,
           ice: {grams = 15, side? (m; default from the mass), film = film.t, Tmelt = 0} | null,
           tMax = 4 h, untilGone = true (stop 120 s after the ice is gone, the lump takes over), store = adaptive} */
    constructor(o = {}) {
      const M = this.mat = mat(o.material || 'aluminum');
      if (M.e === null) throw new Error(`thermo.Slab: ${M.id} has no k, ρ, c`);
      const w = o.w ?? cv('block.w'), H = o.h ?? cv('block.h'), dr = o.dr ?? 0.002;
      const R = this.R = w / Math.sqrt(Math.PI), nr = this.nr = Math.max(4, Math.round(R / dr)), DR = this.dr = R / nr;
      const dzmm = o.dz || DZ_DEFAULT, sum = dzmm.reduce((s, x) => s + x, 0);
      const DZ = this.dz = dzmm.map(x => x / sum * H), nz = this.nz = DZ.length;
      this.H = H; this.w = w;
      this.zTop = []; { let z = 0; for (const d of DZ) { this.zTop.push(z); z += d; } }
      const Tair = this.Tair = o.Tair ?? cv('T.room'), hA = this.hAir = o.hAir ?? cv('h.still'), T0 = this.T0 = o.T0 ?? Tair;
      const N = this.n = nr * nz, km = M.k, rc = M.rho * M.c;
      // per cell (index j * nr + i): capacity, conductances to the right (i+1) and below (j+1), to air
      const Cc = new Float64Array(N), Gr = new Float64Array(N), Gd = new Float64Array(N), Gair = new Float64Array(N);
      const ring = i => Math.PI * DR * DR * ((i + 1) * (i + 1) - i * i);            // top area of ring i
      for (let j = 0; j < nz; j++) for (let i = 0; i < nr; i++) {
        const id = j * nr + i;
        Cc[id] = rc * ring(i) * DZ[j];
        if (i < nr - 1) Gr[id] = km * 2 * Math.PI * (i + 1) * DR * DZ[j] / DR;
        if (j < nz - 1) Gd[id] = km * ring(i) / ((DZ[j] + DZ[j + 1]) / 2);
        let g = 0;
        if (i === nr - 1) g += 2 * Math.PI * R * DZ[j] / (1 / hA + DR / (2 * km));    // the rim
        Gair[id] = g;                                                                   // top added below (ice-aware)
      }
      // top surface: ring i area split into the ice patch (area under the cube) and air
      const ice = o.ice === null ? null : { grams: 15, ...(o.ice || {}) };
      this.ice0 = ice ? ice.grams / 1000 : 0;
      const side = ice ? (ice.side ?? Math.cbrt(this.ice0 / cv('rho.ice'))) : 0;
      this.iceSide = side;
      const rp = this.rPatch = ice ? side / Math.sqrt(Math.PI) : 0;                     // patch of equal area
      const film = ice ? (ice.film ?? cv('film.t')) : 0;
      this.film = film;
      const Gtop = new Float64Array(nr), Gice = new Float64Array(nr);
      for (let i = 0; i < nr; i++) {
        const a0 = i * DR, a1 = (i + 1) * DR, full = Math.PI * (a1 * a1 - a0 * a0);
        const cov = rp <= a0 ? 0 : Math.PI * (Math.min(rp, a1) ** 2 - a0 * a0);
        Gice[i] = cov / (DZ[0] / (2 * km) + film / cv('k.water'));
        Gtop[i] = (full - cov) / (1 / hA + DZ[0] / (2 * km));
        this._Gtopfull = this._Gtopfull || new Float64Array(nr);
        this._Gtopfull[i] = full / (1 / hA + DZ[0] / (2 * km));
      }
      this.Gice = Gice;
      // stability
      let dtMax = Infinity;
      for (let j = 0; j < nz; j++) for (let i = 0; i < nr; i++) {
        const id = j * nr + i;
        let g = Gr[id] + Gd[id] + Gair[id] + (i > 0 ? Gr[id - 1] : 0) + (j > 0 ? Gd[id - nr] : 0);
        if (j === 0) g += Math.max(Gtop[i] + Gice[i], this._Gtopfull[i]);
        dtMax = Math.min(dtMax, Cc[id] / g);
      }
      this.dt = 0.9 * dtMax;
      this.Ctot = Cc.reduce((s, x) => s + x, 0);
      this._run(o, { Cc, Gr, Gd, Gair, Gtop, Gice });
    }
    _run(o, { Cc, Gr, Gd, Gair, Gtop, Gice }) {
      const nr = this.nr, nz = this.nz, N = this.n, Tair = this.Tair, Tm = 0;
      let T = new Float64Array(N).fill(this.T0), Tn = new Float64Array(N);
      const tMax = o.tMax ?? 4 * 3600, dt = this.dt, Gfull = this._Gtopfull;
      const rhoI = cv('rho.ice'), hA = this.hAir, side = this.iceSide;
      let m = this.ice0, t = 0, Qbi = 0, Qai = 0, Qab = 0, tGone = m > 0 ? null : 0, lumped = false;
      const E0 = this.E0 = this.Ctot * this.T0;
      // frames: adaptive store interval (dense while things change fast)
      const frames = this.frames = [];
      const storeAt = t => (t < 60 ? 0.5 : t < 300 ? 1 : t < 1800 ? 5 : 30);
      let nextStore = 0;
      const push = () => frames.push({ t, T: Float32Array.from(T), grams: m * 1000, Qbi, Qai, Qab, E: dotC(T) });
      const dotC = Tv => { let s = 0; for (let q = 0; q < N; q++) s += Cc[q] * Tv[q]; return s; };
      // the ice's exposed area to air: the top and 4 sides of a cube with a fixed footprint (side²) that loses height
      const iceAir = mm => (mm > 0 ? side * side + 4 * side * (mm / (rhoI * side * side)) : 0);
      const Gairtot = (() => { let g = 0; for (let i = 0; i < nr; i++) g += Gfull[i]; for (let q = 0; q < N; q++) g += Gair[q]; return g; })();
      let steps = 0;
      const t0 = Date.now();
      while (t < tMax) {
        if (t >= nextStore - 1e-9) { push(); nextStore = t + storeAt(t); }
        if (tGone !== null && !lumped && o.untilGone !== false && t > tGone + 120) {
          let lo = Infinity, hi = -Infinity; for (let q = 0; q < N; q++) { lo = Math.min(lo, T[q]); hi = Math.max(hi, T[q]); }
          const mean = dotC(T) / this.Ctot;
          if (hi - lo < Math.max(0.02, 0.05 * Math.abs(mean - Tair))) lumped = true;
        }
        if (lumped) {
          // exact exponential of the uniform block toward the air, to the next store time
          const span = Math.min(nextStore, tMax) - t, mean = dotC(T) / this.Ctot;
          const nm = Tair + (mean - Tair) * Math.exp(-span * Gairtot / this.Ctot), d = nm - mean;
          for (let q = 0; q < N; q++) T[q] += d;
          Qab += this.Ctot * d; t += span; continue;
        }
        const iceOn = m > 0;
        let qbi = 0, qab = 0;
        for (let j = 0; j < nz; j++) for (let i = 0; i < nr; i++) {
          const id = j * nr + i, Ti = T[id];
          let q = 0, qa = Gair[id] * (Tair - Ti);
          if (i < nr - 1) q += Gr[id] * (T[id + 1] - Ti);
          if (i > 0) q += Gr[id - 1] * (T[id - 1] - Ti);
          if (j < nz - 1) q += Gd[id] * (T[id + nr] - Ti);
          if (j > 0) q += Gd[id - nr] * (T[id - nr] - Ti);
          if (j === 0) {
            if (iceOn) { const qi = Gice[i] * (Ti - Tm); q -= qi; qbi += qi; qa += Gtop[i] * (Tair - Ti); }
            else qa += Gfull[i] * (Tair - Ti);
          }
          qab += qa;
          Tn[id] = Ti + dt * (q + qa) / Cc[id];
        }
        [T, Tn] = [Tn, T];
        Qbi += qbi * dt; Qab += qab * dt;
        if (iceOn) {
          const qai = hA * iceAir(m) * (Tair - Tm);
          const dE = (qbi + qai) * dt, dm = dE / Lf;
          if (dm >= m) {
            // finishes inside this step: book only the share that melts the rest; the block's share is returned to it
            const f = m / dm;
            tGone = t + dt * f;
            Qai += qai * dt * f;
            const back = qbi * dt * (1 - f);
            Qbi -= back;                                                  // that heat never left the block:
            // put it back proportionally to the patch cells it came from (Tn holds the pre-step temperatures)
            for (let i = 0; i < nr; i++) if (Gice[i] > 0) { const share = Gice[i] * (Tn[i] - Tm) / Math.max(qbi, 1e-30); T[i] += back * share / Cc[i]; }
            m = 0;
          } else { m -= dm; Qai += qai * dt; }
        }
        t += dt; steps++;
        if (tGone !== null && o.untilGone === false && t > tGone + (o.after ?? 600)) break;
      }
      push();
      this.tEnd = t; this.tGone = tGone; this.steps = steps; this.ms = Date.now() - t0;
      this.frameTimes = frames.map(f => f.t);
    }
    _frame(t) {                                    // index k and weight u with frames[k].t ≤ t ≤ frames[k+1].t
      const F = this.frameTimes, n = F.length;
      if (t <= F[0]) return [0, 0];
      if (t >= F[n - 1]) return [n - 1, 0];
      let lo = 0, hi = n - 1;
      while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (F[mid] <= t) lo = mid; else hi = mid; }
      return [lo, (t - F[lo]) / (F[hi] - F[lo])];
    }
    // after the end of the stored run, the block relaxes to the air as one lump (exact)
    _tail(t) {
      const f = this.frames[this.frames.length - 1], mean = f.E / this.Ctot;
      const G = (() => { let g = 0; for (let i = 0; i < this.nr; i++) g += this._Gtopfull[i]; g += 2 * Math.PI * this.R * this.H / (1 / this.hAir + this.dr / (2 * this.mat.k)); return g; })();
      return this.Tair + (mean - this.Tair) * Math.exp(-(t - f.t) * G / this.Ctot) - mean;   // the shift to add
    }
    /* sample(t, out?) → {t, grams, melted (g), frac (melted share), gone (bool), T (Float32Array nr × nz, row j = layer
       from the top, column i = ring from the center), mean, min, Qblock (J block → ice), Qair (J air → ice),
       Qairblock (J air → block), Eblock (J above 0 °C)}. Pure: interpolates the stored frames. */
    sample(t, out) {
      t = Math.max(0, t);
      const [kk, u] = this._frame(t), a = this.frames[kk], b = this.frames[Math.min(kk + 1, this.frames.length - 1)];
      const N = this.n, Tv = out && out.T && out.T.length === N ? out.T : new Float32Array(N);
      const past = t > this.frames[this.frames.length - 1].t, shift = past ? this._tail(t) : 0;
      let mn = Infinity, s = 0;
      for (let q = 0; q < N; q++) { const v = a.T[q] + (b.T[q] - a.T[q]) * u + shift; Tv[q] = v; if (v < mn) mn = v; }
      const L = (x, y) => x + (y - x) * u;
      const grams = Math.max(0, L(a.grams, b.grams));
      const E = L(a.E, b.E) + shift * this.Ctot;
      s = E / this.Ctot;
      const o = out || {};
      Object.assign(o, { t, grams, melted: this.ice0 * 1000 - grams, frac: this.ice0 ? 1 - grams / (this.ice0 * 1000) : 0, gone: this.tGone !== null && t >= this.tGone,
        T: Tv, mean: s, min: mn, Qblock: L(a.Qbi, b.Qbi), Qair: L(a.Qai, b.Qai), Qairblock: L(a.Qab, b.Qab) + shift * this.Ctot, Eblock: E });
      return o;
    }
    // the model temperature at a point: x across (m, −R … R, 0 = under the cube's center), z down from the top (m)
    at(t, x, z) {
      const i = Math.min(this.nr - 1, Math.floor(Math.abs(x) / this.dr));
      let j = 0; while (j < this.nz - 1 && z >= this.zTop[j + 1]) j++;
      const [kk, u] = this._frame(t), a = this.frames[kk], b = this.frames[Math.min(kk + 1, this.frames.length - 1)], q = j * this.nr + i;
      return a.T[q] + (b.T[q] - a.T[q]) * u + (t > this.frames[this.frames.length - 1].t ? this._tail(t) : 0);
    }
    gramsAt(t) { return this.sample(t).grams; }
    // the first time the ice has lost `share` of its mass (null if never in the run)
    tMelted(share) {
      const want = this.ice0 * 1000 * (1 - share), F = this.frames;
      for (let q = 1; q < F.length; q++) if (F[q].grams <= want) { const a = F[q - 1], b = F[q]; return a.t + (b.t - a.t) * (a.grams - want) / Math.max(1e-12, a.grams - b.grams); }
      return null;
    }
    geometry() { return { R: this.R, H: this.H, w: this.w, nr: this.nr, nz: this.nz, dr: this.dr, dz: this.dz.slice(), zTop: this.zTop.slice(), rPatch: this.rPatch, iceSide: this.iceSide }; }
  }
  const raceCache = {};
  // iceRace(material, opts) → a Slab with a cube on top; cached per (material, opts) so a scene can call it every frame
  function iceRace(material, o = {}) {
    const key = mat(material).id + JSON.stringify(o);
    return raceCache[key] || (raceCache[key] = new Slab({ material, ...o, ice: { grams: (o.grams ?? cv('m.cube') * 1000), film: o.film, side: o.side } }));
  }

  /* ======================================================================
     7. Layers in series: wrapped ice and the cooler share this
     ====================================================================== */
  const FABRIC = { hoodie: { material: 'wool', t: cv('t.hoodie') }, towel: { material: 'cotton', t: cv('t.towel') } };
  // R (m²·°C/W) of a wall: an outside air film + layers [{material|k, t}] + an inside film (h or null)
  function seriesR(layers, hOut, hIn) {
    let Rv = (hOut ? 1 / hOut : 0) + (hIn ? 1 / hIn : 0);
    for (let L of layers || []) {
      if (typeof L === 'string') { if (L === 'none' || L === 'bare') continue; L = FABRIC[L]; if (!L) throw new Error('thermo: unknown layer'); }
      const kk = L.k ?? mat(L.material).k;
      Rv += L.t / kk;
    }
    return Rv;
  }
  /* wrapped(layers, mIce = 1 kg, Tair = T.room, {h}) : a cube of ice at 0 °C on a table (bottom insulated), its top and
     4 sides covered by `layers` (e.g. ['hoodie'], [] = bare). Heat leak = A(m) ΔT / R; A = 5 side² shrinks as it melts,
     so m^(1/3) falls linearly in time (closed form):  m(t)^(1/3) = m0^(1/3) − κ t,  κ = 5 ΔT / (3 R Lf ρ^(2/3)). */
  function wrapped(layers = [], mIce = cv('m.iceBlock'), Tair = cv('T.room'), o = {}) {
    const h = o.h ?? cv('h.still'), Rv = seriesR(layers, h, null), rI = cv('rho.ice'), dT = Tair - 0;
    const kap = dT > 0 ? 5 * dT / (3 * Rv * Lf * Math.pow(rI, 2 / 3)) : 0, c0 = Math.cbrt(mIce);
    const tGone = kap > 0 ? c0 / kap : Infinity;
    const massAt = t => Math.pow(Math.max(0, c0 - kap * Math.max(0, t)), 3);
    return {
      R: Rv, U: 1 / Rv, tGone, m0: mIce, Tair, layers: (layers || []).slice(),
      leak0: 5 * Math.pow(mIce / rI, 2 / 3) * dT / Rv,
      sample(t) {
        const m = massAt(t), A_ = 5 * Math.pow(m / rI, 2 / 3);
        return { t, kg: m, grams: m * 1000, melted: (mIce - m) * 1000, leakW: m > 0 ? A_ * dT / Rv : 0, Q: (mIce - m) * Lf, gone: m <= 0 };
      },
      tMelted(share) { return Math.pow(c0 - Math.cbrt(mIce * (1 - share)), 1) / kap; },
    };
  }

  /* ======================================================================
     8. The stove: a stirred pot on a steady burner (rows 5.8–5.10, 6.8)
     ====================================================================== */
  /* stove({mIce = 1, mWater = 0, T0 = −10, power = 1000 | [[t, W], …], boil = 100}) → phases + pure sample(t).
     Start states: all ice below 0 (mWater = 0), ice + water at 0 °C, or water above 0 (mIce = 0). The pot is stirred
     (Correction 13), so the whole pot has one temperature. No heat lost to the room (row 6.8: real times are longer). */
  function stove(o = {}) {
    const mI = o.mIce ?? 1, mW = o.mWater ?? 0, T0 = o.T0 ?? (mW > 0 && mI > 0 ? 0 : -10), Tb = o.boil ?? cv('T.boilClass');
    if (mI > 0 && mW > 0 && T0 !== 0) throw new Error('thermo.stove: ice and water together start at 0 °C');
    if (mI > 0 && T0 > 0) throw new Error('thermo.stove: ice above 0 °C');
    const M = mI + mW, ph = [];
    let Q = 0;
    const add = (name, flat, J, Ta, Tz) => { if (J > 0) { ph.push({ name, flat, Q0: Q, Q1: Q + J, T0: Ta, T1: Tz }); Q += J; } };
    if (mI > 0 && T0 < 0) add('ice', false, mI * CI * (0 - T0), T0, 0);
    if (mI > 0) add('melt', true, mI * Lf, 0, 0);
    add('water', false, M * CW * (Tb - Math.max(0, T0)), Math.max(0, T0), Tb);
    add('boil', true, M * Lv, Tb, Tb);
    const Qend = Q;
    // the power schedule → energy delivered by time t (pure)
    const sched = Array.isArray(o.power) ? o.power.slice().sort((a, b) => a[0] - b[0]) : [[0, o.power ?? cv('P.burner')]];
    if (sched[0][0] > 0) sched.unshift([0, 0]);
    const Qat = t => { let q = 0; for (let i = 0; i < sched.length; i++) { const a = sched[i][0], b = i + 1 < sched.length ? sched[i + 1][0] : Infinity; if (t <= a) break; q += sched[i][1] * (Math.min(t, b) - a); } return q; };
    const tAtQ = q => { let acc = 0; for (let i = 0; i < sched.length; i++) { const a = sched[i][0], b = i + 1 < sched.length ? sched[i + 1][0] : Infinity, P = sched[i][1]; const seg = P * (b - a); if (P > 0 && acc + seg >= q) return a + (q - acc) / P; acc += seg; } return Infinity; };
    function stateAt(q) {
      q = Math.max(0, Math.min(q, Qend));
      let p = ph.find(x => q <= x.Q1 + 1e-9) || ph[ph.length - 1];
      const f = p.Q1 > p.Q0 ? (q - p.Q0) / (p.Q1 - p.Q0) : 1;
      const T = p.T0 + (p.T1 - p.T0) * f;
      let ice = mI, water = mW, steam = 0;
      for (const x of ph) {
        const g = q >= x.Q1 ? 1 : q <= x.Q0 ? 0 : (q - x.Q0) / (x.Q1 - x.Q0);
        if (x.name === 'melt') { ice -= mI * g; water += mI * g; }
        if (x.name === 'boil') { water -= M * g; steam += M * g; }
      }
      return { T, phase: p.name, flat: p.flat, ice, water, steam, Q: q, dry: q >= Qend - 1e-9 };
    }
    return {
      phases: ph.map(p => ({ ...p, t0: tAtQ(p.Q0), t1: tAtQ(p.Q1), seconds: tAtQ(p.Q1) - tAtQ(p.Q0) })),
      flats: ph.filter(p => p.flat).map(p => ({ name: p.name, T: p.T0, seconds: tAtQ(p.Q1) - tAtQ(p.Q0), J: p.Q1 - p.Q0 })),
      Qend, tEnd: tAtQ(Qend), boil: Tb, start: { mIce: mI, mWater: mW, T0 },
      at: stateAt,                                            // the state after q joules went in (any power schedule)
      sample(t) { const q = Qat(Math.max(0, t)); const s = stateAt(q); s.t = t; s.Qin = Math.min(q, Qend); s.burnerJ = q; return s; },
      phase(t) { return stateAt(Qat(Math.max(0, t))).phase; },
      // energy bookkeeping: what the state holds above the start (sensible + latent), J
      stored(s) {
        let E = 0;
        for (const x of ph) { const g = s.Q >= x.Q1 ? 1 : s.Q <= x.Q0 ? 0 : (s.Q - x.Q0) / (x.Q1 - x.Q0); E += g * (x.Q1 - x.Q0); }
        return E;
      },
      // an independent count from the state's masses and temperature (thermocheck)
      bookkeep(s) {
        const meltedIce = mI - s.ice, Tn = s.T;
        let E = 0;
        if (mI > 0 && T0 < 0) E += mI * CI * (Math.min(Tn, 0) - T0);
        E += meltedIce * Lf + s.steam * Lv;
        if (Tn > Math.max(0, T0)) E += M * CW * (Tn - Math.max(0, T0));
        return E;
      },
    };
  }
  // the number of flat stretches on the stirred pot's graph from T0 (°C) up to boiling away (pool formulas)
  // the temperature of a flat stretch of the stirred pot: 'melt' → 0, 'boil' → 100 (the model's plateaus)
  const plateau = name => { const f = stove({ mIce: 1, T0: -10 }).flats.find(x => x.name === name); if (!f) throw new Error(`thermo.plateau: "${name}"`); return f.T; };
  const stoveFlats = (T0, mIce = 1, mWater = 0) => stove({ T0, mIce: T0 > 0 ? 0 : mIce, mWater: T0 > 0 ? mIce + mWater : mWater }).flats.length;

  /* ======================================================================
     9. Drop vs bath (row 6.7)
     ====================================================================== */
  function dropVsBath(o = {}) {
    const Tr = o.Troom ?? cv('T.roomBath');
    const one = (m, T) => {
      const toRoom = m * CW * (T - Tr), toZero = m * CW * (T - 0);
      // tipped onto a pile of ice at 0 °C, the water cools all the way to 0 °C: the ice that energy melts
      return { m, T, toRoomJ: toRoom, toZeroJ: toZero, iceKg: toZero / Lf, iceGrams: toZero / Lf * 1000, iceKgToRoom: toRoom / Lf, iceGramsToRoom: toRoom / Lf * 1000 };
    };
    const drop = one(o.mDrop ?? cv('m.drop'), o.Tdrop ?? cv('T.drop')), bath = one(o.mBath ?? cv('m.bath'), o.Tbath ?? cv('T.bath'));
    return { Troom: Tr, drop, bath, ratio: bath.toRoomJ / drop.toRoomJ, ratioIce: bath.iceKg / drop.iceKg, cocoaJ: 0.25 * CW * (90 - Tr) };
  }

  /* ======================================================================
     10. The cooler game (DESIGN.md §5.3): invented sizes and prices, sourced physics
     ====================================================================== */
  /* A box of fixed OUTSIDE size (assumed). Walls + bottom (and the lid, if any) are one build: a wall material at a
     thickness, plus an optional lining on the walls and bottom (not the lid). Heat flows IN through every face:
       face leak (W) = A_face (Tair − 0) / R,  R = 1/h_out (breeze) + t_wall/k_wall + t_lining/k_lining + 1/h_in (still)
     An open top: the breeze reaches the contents directly: R = 1/h_out. A_face is taken at the wall's mid-plane.
     Everything inside starts at 0 °C (assumed) and stays at 0 °C while any ice is left (stirred enough by walking).
     The loose ice melts first; the pops are frozen while loose ice is left (delivered); then the pops melt (soft);
     when they are all melted it is juice. Space: the ice and pops must fit inside (invented packing volumes). */
  const COOLER = {
    outer: cv('cooler.outer'),                                            // [W, D, H] m outside
    walls: {
      metal: { material: 'aluminum', name: 'metal', thin: 0.001, thick: 0.003, cost: { thin: 2, thick: 3 }, lid: { thin: 1, thick: 1 } },
      plastic: { material: 'hdpe', name: 'plastic', thin: 0.003, thick: 0.008, cost: { thin: 3, thick: 4 }, lid: { thin: 1, thick: 2 } },
      foam: { material: 'foam', name: 'foam', thin: 0.02, thick: 0.045, cost: { thin: 4, thick: 7 }, lid: { thin: 2, thick: 2 } },
    },
    linings: { none: { t: 0, cost: 0 }, towel: { ...FABRIC.towel, cost: 1 }, hoodie: { ...FABRIC.hoodie, cost: 2 } },
    ice: { scoop: 0.25, cost: 3, litersPerScoop: 0.5, maxScoops: 12 },   // a scoop: 250 g of loose cubes, 0.5 L, 3 tickets
    pop: { kg: cv('m.pop'), liters: 0.1 },                               // a wrapped pop takes 0.1 L
    hOut: cv('h.breeze'), hIn: cv('h.still'), Tair: cv('T.orlandoJuly'),
    trips: [
      { id: 1, name: 'the far gate', minutes: 30, pops: 8, budget: 16 },
      { id: 2, name: 'a big order for the water park', minutes: 120, pops: 32, budget: 14 },
      { id: 3, name: 'a four-hour shift at the lake stand', minutes: 240, pops: 6, budget: 13 },
    ],
    designs: {
      max: { wall: 'metal', thick: 'thin', lining: 'none', lid: false, ice: 0 },          // Max's: all metal, no lid, no ice
      starter: { wall: 'foam', thick: 'thin', lining: 'none', lid: true, ice: 1 },       // sensible: wins trip 1 only
      bigOrder: { wall: 'foam', thick: 'thin', lining: 'towel', lid: true, ice: 2 },     // wins trips 1 and 2, not 3
      longHaul: { wall: 'foam', thick: 'thick', lining: 'towel', lid: true, ice: 1 },    // wins trips 1 and 3; no room for trip 2
    },
  };
  function coolerDims(d) {
    const W = COOLER.walls[d.wall]; if (!W) throw new Error(`thermo.cooler: wall "${d.wall}"`);
    const t = W[d.thick || 'thin'], lin = COOLER.linings[d.lining || 'none'], tl = lin.t || 0, [OW, OD, OH] = COOLER.outer;
    const iw = OW - 2 * t - 2 * tl, id_ = OD - 2 * t - 2 * tl, ih = OH - t - tl - (d.lid ? t : 0);
    return { t, tl, inside: [iw, id_, ih], liters: Math.max(0, iw * id_ * ih) * 1000, W, lin };
  }
  function cooler(d, trip) {
    const tr = typeof trip === 'object' ? trip : COOLER.trips.find(x => x.id === trip);
    if (!tr) throw new Error(`thermo.cooler: no trip ${trip}`);
    const { t, tl, inside, liters, W, lin } = coolerDims(d), [OW, OD, OH] = COOLER.outer;
    const Tair = tr.Tair ?? COOLER.Tair, dT = Tair - 0;
    const layers = [{ material: W.material, t }].concat(tl ? [{ material: lin.material, t: tl }] : []);
    const Rwall = seriesR(layers, COOLER.hOut, COOLER.hIn), Rlid = seriesR([{ material: W.material, t }], COOLER.hOut, COOLER.hIn), Ropen = 1 / COOLER.hOut;
    // mid-plane face areas of the wall build
    const mw = OW - t, md = OD - t, mh = OH - t / 2 - (d.lid ? t / 2 : 0);
    const faces = {
      sides: 2 * (mw * mh + md * mh), bottom: mw * md,
      top: d.lid ? mw * md : (OW - 2 * t) * (OD - 2 * t),
    };
    const leak = {
      sides: faces.sides * dT / Rwall, bottom: faces.bottom * dT / Rwall,
      top: d.lid ? faces.top * dT / Rlid : faces.top * dT / Ropen,
    };
    const W_ = leak.sides + leak.bottom + leak.top;
    const iceKg = (d.ice || 0) * COOLER.ice.scoop, popsKg = tr.pops * COOLER.pop.kg;
    const need = (d.ice || 0) * COOLER.ice.litersPerScoop + tr.pops * COOLER.pop.liters;
    const cost = W.cost[d.thick || 'thin'] + (d.lid ? W.lid[d.thick || 'thin'] : 0) + lin.cost + (d.ice || 0) * COOLER.ice.cost;
    const tIce = iceKg * Lf / W_, tAll = (iceKg + popsKg) * Lf / W_, T = tr.minutes * 60;
    const fits = need <= liters + 1e-9, affordable = cost <= tr.budget;
    const status = !fits ? 'no room' : T <= tIce ? 'frozen' : T < tAll ? 'soft' : 'juice';
    const sample = s => {
      s = Math.max(0, s);
      const melt = Math.min(iceKg + popsKg, W_ * s / Lf), ice = Math.max(0, iceKg - melt), pops = Math.max(0, popsKg - Math.max(0, melt - iceKg));
      return { t: s, iceKg: ice, popsKg: pops, meltKg: melt, Tin: ice + pops > 0 ? 0 : null, leakW: ice + pops > 0 ? W_ : 0, Q: melt * Lf,
        state: ice > 0 ? 'frozen' : pops > 0 ? (pops < popsKg ? 'soft' : 'frozen') : 'juice', leak };
    };
    return {
      design: { ...d }, trip: tr, Tair, faces, leak, leakW: W_, R: { wall: Rwall, lid: Rlid, open: Ropen },
      liters, need, fits, cost, affordable, budget: tr.budget,
      iceKg, popsKg, minutesIce: tIce / 60, minutesAll: tAll / 60, spare: (tIce - T) / 60,
      status, delivered: fits && status === 'frozen', ok: fits && affordable && status === 'frozen',
      sample, inside,
    };
  }
  // every design the workbench can build (for tools and a "best record" search)
  function coolerDesigns() {
    const out = [];
    for (const wall of Object.keys(COOLER.walls)) for (const thick of ['thin', 'thick']) for (const lining of Object.keys(COOLER.linings))
      for (const lid of [false, true]) for (let ice = 0; ice <= COOLER.ice.maxScoops; ice++) out.push({ wall, thick, lining, lid, ice });
    return out;
  }

  /* ======================================================================
     11. The fin (case beats c7–c8) and the brake run (heat view of the station)
     ====================================================================== */
  /* One Kanonen brake fin (brass, rows 3.45, 7.3) starts fin.dT0 warmer than the air of a brake shed (assumed) and
     shares heat with that air through both faces (h = breeze). Lumped: the fin's Biot number h·b/(2k) is tiny. The
     measured cooling right after a train is about 0.10 °C/s (row 7.4): our convection-only model cools more slowly
     (it leaves out conduction into the track), so on screen it is "our model", and no rate is quoted. */
  function finScene() {
    const L = cv('fin.L'), h = cv('fin.h'), b = cv('fin.b'), mFin = L * h * b * cv('rho.brass');
    const Tair = cv('T.finAir'), fin = { m: mFin, c: c.brass, T: Tair + cv('fin.dT0') };
    const air = { m: cv('fin.airVolume') * cv('rho.air'), c: c.air, T: Tair };
    const hA = cv('h.breeze') * 2 * L * h;
    const ex = exchange(fin, air, hA);
    return { fin, air, hA, mFin, area: 2 * L * h, ex, sample: t => ex.sample(t), tSame: tol => ex.tSame(tol), rate0: hA * (fin.T - air.T) / (mFin * c.brass) };
  }
  /* the brake run of The Big Drop (`s3`), dressed with Kanonen-style brass fins (rows 3.45, 7.3: a pair of 0.2 m ×
     6 mm fins per meter of track, our assumption for this invented coaster): the heat booked per meter by the coaster
     model spread through the fins of that meter → their temperature rise. Needs core-local/coaster.js. */
  function brakeRun(id = 's3') {
    const Co = root.Coaster || (typeof require === 'function' ? require('./coaster.js') : null);
    if (!Co) throw new Error('thermo.brakeRun: core-local/coaster.js is not loaded');
    const H = Co.heatAlong(id), kgPerM = 2 * cv('fin.h') * cv('fin.b') * cv('rho.brass'), CperM = kgPerM * c.brass;
    const rise = H.perMeter.map(([x, J]) => [x, J / CperM]);
    return { x0: H.x0, x1: H.x1, kgPerMeter: kgPerM, perMeter: H.perMeter, rise, maxRise: Math.max(...rise.map(r => r[1])), riseAt: x => H.at(x) / CperM };
  }

  /* ======================================================================
     12. Heat view colors (DESIGN.md §2): one fixed 0–100 °C scale + per-scene scales
     ====================================================================== */
  /* Palette stops (°C → sRGB hex), interpolated in OKLab (perceptually even): deep blue at 0, pale near room
     temperature (21), a clear peach by 33 (so 22 and 33 differ at a glance), orange, red, deep red at 100. */
  const STOPS = [[0, '#17306f'], [8, '#2f5fae'], [15, '#7ea6d8'], [21, '#efece2'], [27, '#f6d2a2'], [33, '#f3ad6b'], [45, '#e8803f'], [62, '#d4512b'], [80, '#b02a22'], [100, '#6e0f16']];
  const hex = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  const lin = u => { u /= 255; return u <= 0.04045 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4); };
  const delin = u => { const v = u <= 0.0031308 ? 12.92 * u : 1.055 * Math.pow(u, 1 / 2.4) - 0.055; return Math.max(0, Math.min(255, Math.round(v * 255))); };
  function toLab([R, G, B]) {
    const r = lin(R), g_ = lin(G), b = lin(B);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g_ + 0.0514459929 * b), m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g_ + 0.1073969566 * b), s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g_ + 0.6299787005 * b);
    return [0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s, 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s];
  }
  function fromLab([L, a, b]) {
    const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3), m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3), s = Math.pow(L - 0.0894841775 * a - 1.2914855480 * b, 3);
    return [delin(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s), delin(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s), delin(-0.0041960863 * l - 0.7034186147 * m + 1.7076998372 * s)];
  }
  const LABS = STOPS.map(([T, h]) => [T, toLab(hex(h))]);
  const LUT = [];                                   // 0.1 °C steps, 0 … 100
  for (let i = 0; i <= 1000; i++) {
    const T = i / 10; let q = 0; while (q < LABS.length - 2 && T > LABS[q + 1][0]) q++;
    const [Ta, A_] = LABS[q], [Tb, B_] = LABS[q + 1], u = Math.max(0, Math.min(1, (T - Ta) / (Tb - Ta)));
    const rgb = fromLab([0, 1, 2].map(j => A_[j] + (B_[j] - A_[j]) * u));
    LUT.push({ rgb, css: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` });
  }
  const ROOM = 21;                                   // the palette's pale point (°C)
  const lutAt = T => LUT[Math.max(0, Math.min(1000, Math.round(T * 10)))];
  const Heat = {
    lo: 0, hi: 100, room: ROOM, stops: STOPS.map(s => s.slice()),
    // the fixed scale: T (°C) → {rgb: [r, g, b], css: 'rgb(r,g,b)'}; below 0 = the 0 color, above 100 = the 100 color
    color: T => lutAt(T),
    rgb: T => lutAt(T).rgb, css: T => lutAt(T).css,
    lab: T => toLab(lutAt(T).rgb),
    ticks: [0, 20, 40, 60, 80, 100],
    /* a scene's own scale for a narrow range: lo … hi (°C). The scene's room temperature (default 22, clamped into
       [lo, hi]) stays pale, and ONE stretch (palette degrees per scene degree) is used on both sides, the largest that
       keeps lo and hi inside the palette: so equal steps in °C look like equal steps in color on both sides of room,
       and a scene that is mostly cooler than room (the ice race) is not painted red 3 °C above room.
       → {lo, hi, room, stretch, color(T), rgb(T), css(T), ticks, label} */
    scale(lo, hi, o = {}) {
      if (!(hi > lo)) throw new Error('Heat.scale: hi > lo');
      const room = Math.max(lo, Math.min(hi, o.room ?? 22));
      const sCold = room > lo ? ROOM / (room - lo) : Infinity, sWarm = hi > room ? (100 - ROOM) / (hi - room) : Infinity;
      const s = Math.min(sCold, sWarm);
      const color = T => lutAt(ROOM + (Math.max(lo, Math.min(hi, T)) - room) * s);
      const span = hi - lo, stepsOK = [0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10, 20, 25, 50];
      const st = stepsOK.find(x => span / x <= 6) || 50, ticks = [];
      for (let v = Math.ceil(lo / st - 1e-9) * st; v <= hi + 1e-9; v += st) ticks.push(+v.toFixed(2));
      return { lo, hi, room, stretch: s, color, rgb: T => color(T).rgb, css: T => color(T).css, ticks, label: o.label || Heat.label };
    },
    label: 'Heat view: a computer model of the temperatures, not a camera',
  };

  /* ======================================================================
     13. Scenes: fixed parameters the script and the levels read (each names its heat-view range)
     ====================================================================== */
  /* the heat booked by The Big Drop's s3 run at its stop (needs core-local/coaster.js; cached) */
  let RUN_HEAT = null;
  function runHeat() {
    if (RUN_HEAT != null) return RUN_HEAT;
    const Co = root.Coaster || (typeof require === 'function' ? require('./coaster.js') : null);
    if (!Co) throw new Error('thermo: scenes.spread.Q needs core-local/coaster.js');
    return (RUN_HEAT = Co.final('s3').heat);
  }
  const scenes = {
    station: { range: [21.5, 23.5], room: 22, Tair: cv('T.room'), run: 's3', note: 'heat view of the brake run: brakeRun() rises are tiny, so the range is narrow' },
    // Q: the heat of The Big Drop's run at its stop (Coaster.final('s3').heat, about 95,000 J), the heat c6 spreads
    spread: { get Q() { return runHeat(); }, material: 'steel', from: 1, to: 500, range: [22, 30] },
    fin: { range: [20, 36], room: 22 },
    bench: { T: 22, Thot: 60, materials: ['aluminum', 'pine'], skin: cv('T.finger'), range: [15, 40], hotRange: [20, 65], room: 22 },
    iceRace: { blocks: ['aluminum', 'foam'], swap: ['aluminum', 'steel', 'pine', 'hdpe', 'foam'], grams: cv('m.cube') * 1000, T: cv('T.room'), range: [0, 25], room: 22 },
    hoodie: { mIce: cv('m.iceBlock'), T: cv('T.room'), layer: 'hoodie', range: [0, 25], room: 22 },
    stove: { mIce: 0.5, mWater: 0.5, T0: 0, power: cv('P.burner'), high: cv('P.high'), powers: [500, cv('P.burner'), cv('P.high')], labels: { [cv('P.burner')]: 'P.burner', [cv('P.high')]: 'P.high' }, range: [0, 100], room: 22 },
    stove68: { mIce: 1, mWater: 0, T0: -10, power: cv('P.burner'), range: [-10, 100] },
    bath: { T: cv('T.bath'), m: cv('m.bath'), range: [0, 100], room: 20 },
    drop: { T: cv('T.drop'), m: cv('m.drop'), range: [0, 100], room: 20 },
    cooler: { Tair: COOLER.Tair, F: cv('F.orlandoJuly'), range: [0, 35], room: 22 },
  };

  /* ======================================================================
     14. Pool helpers (core/bank.js) — the same as circles' itemSteps
     ====================================================================== */
  function itemSteps(item, params) {
    const B = root.Bank;
    if (!B) throw new Error('thermo.itemSteps: core/bank.js is not loaded');
    const S = item.steps || [];
    return S.map((s, i) => {
      const o = { i, type: s.type, text: B.fill(s.text || '', params), hint: s.hint ? B.fill(s.hint, params) : '', format: s.format || null, last: i === S.length - 1 };
      if (s.type === 'numpad') { o.value = B.eval(String(s.formula), params); o.answerText = B.format(o.value, s.format || 'num'); }
      else if (s.type === 'choice') { o.options = (s.options || []).map(x => B.fill(String(x), params)); o.correct = s.correct; o.answerText = o.options[o.correct]; }
      return o;
    });
  }

  const Thermo = {
    constants: CONST, assumed: ASSUMED, value: cv, materials: MATERIALS, mat, c, k, rho, Lf, Lv,
    num, r2, round: roundTo,
    mix, avg, mixBlock, energy, spread, giveOff, heatBar, given,
    contact, feels, touchFlux, flow, warmer,
    exchange, Slab, iceRace, seriesR, fabric: FABRIC, wrapped, stove, stoveFlats, plateau, dropVsBath,
    COOLER, cooler, coolerDims, coolerDesigns, finScene, brakeRun,
    Heat, scenes, itemSteps,
  };
  root.Thermo = Thermo;
  if (!root.Heat) root.Heat = Heat;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('thermo', Thermo);
  if (typeof module !== 'undefined' && module.exports) module.exports = Thermo;
})(typeof window !== 'undefined' ? window : globalThis);
